/** @jsxImportSource react */
import InlineConversionWizard from '@react/gwizzy/components/InlineConversionWizard';
import { getIsConversionDone } from '@react/gwizzy/helpers/utils';
import { useUser } from '@react/services/auth';
import Accordian from '@react/shared/components/Accordian';
import Avatar from '@react/shared/components/Avatar';
import Spinner from '@react/shared/components/Spinner';
import useTimeout from '@react/shared/hooks/useTimeOut';
import { Button, Link } from '@react/system';
import { Time } from '@sly/icons/react';
import { useState } from 'react';

function AdvisorSection({ agent, heading }) {
  const displayName = agent?.info?.displayName;
  return (
    <div className='flex flex-col items-center justify-center gap-4 text-center'>
      {agent && (
        <div className='flex flex-col items-center justify-center gap-1'>
          <Avatar
            size='l'
            name={displayName}
            path={agent?.gallery?.images?.[0]?.path}
          />

          <div className='mt-2'>
            <h3 className='font-t-l'>{displayName}</h3>
            <div className='font-b-s'>Your Seniorly Advisor</div>
          </div>
        </div>
      )}

      <h2 className='text-xl font-bold tracking-tight'>{heading}</h2>
    </div>
  );
}

function Wrapper({ children }) {
  return (
    <div className='flex min-h-[400px] w-full flex-col items-center justify-between rounded-lg bg-white p-10'>
      {children}
    </div>
  );
}

export default function InlineGwizzyAccordian({
  community,
  eventProps,
  mode = {},
  showLocationStep = false,
  submitText = '',
  skipHomebaseRedirect = false,
  conversionSchemaOverride = 'gwizzy',
}) {
  const [status, setStatus] = useState('loading');
  const agent = community?.partnerAgent;

  const { user } = useUser();

  useTimeout(() => {
    const isConversionDone = getIsConversionDone();

    if (isConversionDone) {
      setStatus('done');
    } else {
      setStatus('started');
    }
  }, 1000);

  const handleStart = () => {
    setStatus('started');
  };

  const isLoading = status === 'loading';
  const isPending = status === 'pending';
  const isStarted = status === 'started';

  return (
    <>
      {isLoading ? (
        <div className='h-[400px]'>
          <div className='flex h-full items-center justify-center bg-white'>
            <Spinner />
          </div>
        </div>
      ) : isPending ? (
        <Wrapper>
          <AdvisorSection
            agent={agent}
            heading='Get your personalized quote now by filling out a quick form.'
          />
          <div className='w-full'>
            <div className='mb-2 flex w-full items-center justify-center gap-2 text-center text-xs text-viridian-base'>
              <Time size='xs' />
              <span className='font-medium'>Takes 1 minute</span>
            </div>

            <Button
              onClick={() => handleStart()}
              className='mx-auto  w-max md:block'
            >
              Start
            </Button>
          </div>
        </Wrapper>
      ) : isStarted ? (
        <div className='h-full min-h-[400px] overflow-y-auto rounded bg-slate-lighter-95  md:block'>
          <InlineConversionWizard
            community={community}
            eventProps={eventProps}
            mode={mode}
            showLocationStep={showLocationStep}
            submitText={submitText}
            skipHomebaseRedirect={skipHomebaseRedirect}
            conversionSchemaOverride={conversionSchemaOverride}
          />
        </div>
      ) : (
        <Wrapper>
          <AdvisorSection
            agent={agent}
            heading={`An advisor will reach out to you ${
              community ? 'with pricing.' : 'to discuss options.'
            }`}
          />
          {user && (
            <Link to='/homebase' underlined>
              Go to homebase
            </Link>
          )}
        </Wrapper>
      )}
    </>
  );
}

export const AccordianWrapper = ({
  title,
  eventProps,
  variant = 'A',
  community,
  sectionProps,
  mode,
  showLocationStep = false,
}) => {
  const { isOpen } = sectionProps || {};
  return (
    <Accordian
      title={title}
      variant={variant}
      openByDefault={!!isOpen}
      eventProps={eventProps}
      renderOnlyIfOpen={true}
      className='!pb-0'
    >
      <InlineGwizzyAccordian
        community={community}
        eventProps={eventProps}
        mode={mode}
        showLocationStep={showLocationStep}
      />
    </Accordian>
  );
};
